import type {Ability} from '../../../../generated/types';
import type {ProcedureProps} from './procedures';

/** число или формула: `{level}*5`, `{Cha}`, `{table:Ярость}`, `{dice:1d8}+{Wis}` */
export type FormulaValue = number | string;

/** `dawn` — заряды магических предметов: на рассвете, лист считает его долгим отдыхом */
export type Recharge = 'shortRest' | 'longRest' | 'shortOrLongRest' | 'dawn';

export type ChooseType =
	| 'ability'
	| 'skill'
	| 'expertise'
	| 'tool'
	| 'instrument'
	| 'gamingSet'
	| 'language'
	| 'weapon'
	| 'spell'
	| 'feat'
	| 'feature'
	| 'subClass'
	/** навык или инструмент на выбор: черта «Умелый» */
	| 'proficiency'
	/** выбор из собственного списка options: тип урона, вариант особенности и т. п. */
	| 'option'
	/** предмет из базы: основа для «Броня +1» и «Оружие +1» */
	| 'item';

/**
 * Когда выбор можно пересмотреть. `eachLevel` — выбор повторяется на каждом уровне класса
 * после первого, поэтому его max умножается на число таких уровней.
 */
export type ChooseChange = 'levelUp' | 'longRest' | 'eachLevel';

export type LanguageKind = 'standard' | 'exotic';

export type ChooseFilters = {
	/** списки заклинаний классов; допускается ссылка на выбор: `{magic-initiate-spell-list}` */
	class?: string[];
	spellLevel?: number[];
	/** школа буквой SpellSchool: A, C, D, E, V, I, N, T */
	school?: string | string[];
	ritual?: boolean;
	/** категории черт буквами: O, G, FS, EB, PP */
	featCategory?: string[];
	featureType?: string | string[];
	/** категории оружия буквами: S, M */
	weaponCategory?: string[];
	/** выбирать можно только из навыков, которыми персонаж уже владеет */
	skillProficient?: boolean;
	/** только инструменты ремесленника */
	artisan?: boolean;
	/** из каких таблиц языков выбирать; по умолчанию только стандартные */
	language?: LanguageKind[];
	/** для type:'item': типы предметов ItemType */
	itemType?: string[];
	/** для type:'item': виды доспехов Light, Medium, Heavy, Shield */
	armorKind?: string[];
};

export type ChooseParams = {
	/** на сколько повышается выбранная характеристика */
	increase?: number;
	/** предел, выше которого повышение не поднимает характеристику */
	cap?: number;
	/** выбранные заклинания всегда подготовлены */
	prepared?: boolean;
	usage?: FormulaValue;
	/** нижняя граница usage */
	min?: number;
	recharge?: Recharge;
	/** заклинательная характеристика выбранных заклинаний: значение или ссылка на выбор */
	spellcastingAbility?: string;
	/** предустановленный вариант выбранной черты: значение её группы с ключом `variantKey` */
	variant?: string;
	/** при повторном взятии черты значение должно отличаться от выбранного раньше */
	unique?: boolean;
	/** пояснение к unique для описания повторяемости черты: «каждый раз должны выбирать новый список заклинаний» */
	uniqueNote?: string;
};

export type ChooseOption = {
	key: string;
	label?: string;
	/** что вариант меняет, когда выбран */
	props?: FeatureProps;
	/** вложенные выборы, доступные после выбора варианта */
	choose?: ChooseGroup[];
};

export type ChooseGroup = {
	/** имя выбора: по нему на выбор ссылаются формулы (`{ability}`) */
	key: string;
	type: ChooseType;
	label: string;
	/** собственный список вариантов; без него варианты берутся из справочника по типу */
	options?: ChooseOption[];
	/** сколько значений выбрать: число или формула (`{table:@Cantrips}`) */
	max?: FormulaValue;
	multiple: boolean;
	default?: string;
	change?: ChooseChange;
	/** сколько вариантов можно заменить за одну смену: волшебник меняет один фокус после Долгого отдыха */
	changeLimit?: number;
	params: ChooseParams;
	filters: ChooseFilters;
};

export type Resource = {
	id: string;
	max?: FormulaValue;
	/** нижняя граница max: «количество, равное модификатору Харизмы (минимум 1)» */
	min?: number;
	die?: FormulaValue;
	recharge?: Recharge;
	/** сколько зарядов возвращает короткий отдых, если ресурс восстанавливается долгим */
	regainsOnShortRest?: number;
	/** сколько зарядов возвращается при восстановлении: `1d6+1`; без него — все */
	regain?: string;
};

export type ActionProps = {
	usage?: FormulaValue;
	/** нижняя граница usage */
	min?: number;
	recharge?: Recharge;
	/** сколько использований возвращает короткий отдых, если действие восстанавливается долгим: Ярость */
	regainsOnShortRest?: number;
	/** ресурс, который тратит действие */
	resource?: string;
	/** что действует, пока эффект действия активен (ярость, облик) */
	props?: FeatureProps;
	/** урон действия: `{dice:1d10}`; растёт через levels */
	damage?: FormulaValue;
	/** тип урона или ссылка на выбор: `{draconicAncestryDamageType}` */
	damageType?: string;
	/** испытание против действия: СЛ формулой `8 + {Con} + {proficiency}` */
	save?: {ability: string; dc: FormulaValue};
	/** область или дистанция: «15-футовый Конус или 30-футовая Линия» */
	area?: string;
	/** бросок лечения, который применяется тем же действием: `{dice:1d10} + {level}` */
	healing?: FormulaValue;
	/** применение бросает кость своего ресурса: Острые слова, манёвры Мастера боевых искусств */
	rollsResourceDie?: boolean;
	/** что делает кнопка «Применить», если это больше траты использования: окно выбора */
	procedure?: ProcedureProps;
};

export type GrantedSpell = {
	spell: string;
	level?: number;
	prepared?: boolean;
	usage?: FormulaValue;
	/** нижняя граница usage */
	min?: number;
	recharge?: Recharge;
	/** заклинательная характеристика: значение или ссылка на выбор */
	ability?: string;
	/** ресурс, который тратит накладывание: заряды предмета */
	resource?: string;
	/** сколько единиц ресурса стоит накладывание */
	cost?: number;
	/** своя СЛ предмета вместо СЛ персонажа */
	saveDc?: number;
	/** свой бонус атаки предмета */
	attackBonus?: number;
};

/**
 * Откуда берутся подготовленные заклинания: `prepared` — из всего списка класса после каждого
 * Долгого отдыха (Жрец, Друид), `spellbook` — из книги заклинаний (Волшебник), `known` — список
 * выбирается при получении уровня и меняется по одному заклинанию (Бард, Чародей, Колдун и др.).
 */
export type SpellcastingMode = 'prepared' | 'spellbook' | 'known';

export type SpellcastingProps = {
	mode: SpellcastingMode;
	/** чей список заклинаний */
	class: string;
	ability: string;
	/** своя таблица заклинательства подкласса: строка — уровень класса, столбцы как у таблицы класса */
	table?: SpellTable;
};

export type SpellTable = {
	columns: string[];
	rows: Array<Array<string | null | undefined> | null | undefined>;
};

export type UnarmoredDefense = {
	base: number;
	/** характеристики, модификатор которых добавляется к КБ; допускается ссылка на выбор */
	abilities: string[];
	/** не действует, если персонаж держит щит: Защита без доспехов Монаха */
	withoutShield?: boolean;
};

/**
 * attack — вместо одной из атак действия Атака: Губительное дыхание;
 * afterShortRest — после Короткого отдыха, показывается в окне отдыха: Магическое восстановление
 */
export const ACTION_KINDS = [
	'action',
	'attack',
	'bonusAction',
	'reaction',
	'magicAction',
	'free',
	'afterShortRest',
] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

export const SENSES = ['darkvision', 'blindsight', 'tremorsense', 'truesight'] as const;
export type Sense = (typeof SENSES)[number];

export const EXTRA_SPEEDS = ['fly', 'swim', 'climb', 'burrow'] as const;
export type ExtraSpeed = (typeof EXTRA_SPEEDS)[number];

/** Что особенность прямо меняет в персонаже */
export type FeatureProps = {
	abilityIncrease?: Partial<Record<Ability, number>> & {cap?: number};
	savingThrowProficiency?: string[];
	skillProficiencies?: string[];
	armorProficiencies?: string[];
	weaponProficiencies?: string[];
	toolsProficiencies?: string[];
	musicalInstrumentProficiencies?: string[];
	languages?: string[];
	savingThrowAdvantage?: string[];
	savingThrowConditionAdvantage?: string[];
	resists?: string[];
	immunities?: string[];
	vulnerabilities?: string[];
	conditionImmunities?: string[];
	darkvision?: FormulaValue;
	blindsight?: FormulaValue;
	tremorsense?: FormulaValue;
	truesight?: FormulaValue;
	/** базовая скорость вместо скорости вида */
	speed?: number;
	speedBonus?: FormulaValue;
	flySpeed?: FormulaValue;
	swimSpeed?: FormulaValue;
	climbSpeed?: FormulaValue;
	burrowSpeed?: FormulaValue;
	acBonus?: FormulaValue;
	/** бонус к КБ, пока надет лёгкий, средний или тяжёлый доспех: черта Защита */
	armoredAcBonus?: FormulaValue;
	unarmoredDefense?: UnarmoredDefense;
	hpMax?: FormulaValue;
	hpIncrementEveryLevel?: FormulaValue;
	/** дополнительные атаки действием Атака; от разных особенностей не складываются */
	extraAttack?: number;
	critRange?: number;
	unarmedDamageDie?: FormulaValue;
	/** безоружный удар может использовать Ловкость вместо Силы */
	unarmedDexterity?: boolean;
	damageBonus?: FormulaValue;
	tempHP?: FormulaValue;
	resources?: Resource[];
	spells?: GrantedSpell[];
	spellcasting?: SpellcastingProps;
	action?: ActionProps;
	attack?: ActionProps;
	bonusAction?: ActionProps;
	reaction?: ActionProps;
	magicAction?: ActionProps;
	free?: ActionProps;
	afterShortRest?: ActionProps;
	usage?: FormulaValue;
	/** нижняя граница usage */
	usageMin?: number;
	recharge?: Recharge;
	afterLongRest?: {heroicInspiration?: number};
	savingThrowBonus?: FormulaValue;
	abilityCheckBonus?: FormulaValue;
	spellAttackBonus?: FormulaValue;
	spellSaveDcBonus?: FormulaValue;
	/** прибавка к бонусу умения: Камень Иун мастерства */
	proficiencyBonus?: number;
	/** характеристика не ниже значения: Рукавицы мощи огра, Пояс силы великана */
	abilityMinimum?: Partial<Record<Ability, number>>;
	/** бонусы к атакам самим этим предметом */
	weapon?: AttackBonusProps;
	/** бонусы к безоружным ударам: Обмотки безоружной мощи */
	unarmedStrike?: AttackBonusProps;
	/** множитель скорости, пока действует эффект: Ускорение, Сапоги скорости */
	speedMultiplier?: number;
	/** на сколько размеров больше считается существо при расчёте грузоподъёмности: Могучее телосложение */
	carryingSizeBonus?: number;
	/** кость к броскам d20: Благословение, Указание */
	rollBonus?: RollBonus[];
	/** преимущество или помеха к броскам: Усиление характеристики */
	rollMode?: RollModeEffect[];
	/** что добавляется или меняется на указанных уровнях */
	levels?: Record<string, FeatureProps>;
	/** прочие значения — переменные, на которые ссылаются формулы (`{draconicAncestryDamageType}`) */
	[variable: string]: unknown;
};

export type AttackBonusProps = {
	attackBonus?: number;
	damageBonus?: number;
};

export type D20RollKind = 'attack' | 'save' | 'check';

export type RollBonus = {
	kind: D20RollKind;
	dice: string;
	/** только для этой характеристики; допускается ссылка на выбор */
	ability?: string;
	/** только для проверок этого навыка; допускается ссылка на выбор */
	skill?: string;
};

export type RollModeEffect = {
	kind: D20RollKind;
	mode: 'advantage' | 'disadvantage';
	ability?: string;
	skill?: string;
};

/**
 * props предмета: `equipped` действует, пока предмет надет или в руках; `attuned` — только при настройке;
 * `used` — эффект применения (зелье, пособие), лист его не применяет сам
 */
export type ItemProps = {
	equipped?: FeatureProps;
	attuned?: FeatureProps;
	used?: FeatureProps;
};
